import React, { useState, useEffect, useId, useCallback, useMemo } from 'react';
import { resolveAccountAvatarDisplay } from '../../utils/photoResolver';
import { useAvatarImageFallback } from '../../hooks/useAvatarImageFallback';
import { useNavigate } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import PhraseRotator from './PhraseRotator';
import StagDiscoverTaglineStrip from './StagDiscoverTaglineStrip';

import { useAuth } from '../../contexts/AuthContext';
import { useUnified } from '../../contexts/UnifiedContext';
import { useCity } from '../../contexts/CityContext';
import { useToast } from '../shared/ToastNotification';
import { getErrorMessage } from '../../services/utils/errorHandler';
import { getUserCoordsOnce, GEO_PERMISSION_DENIED } from '../../utils/userGeolocation';
import { pickNearestLiveCity } from '../../utils/geo';
import { analyticsService } from '../../services/analytics.service';
import { authAPI, getAPIRoot, getCachedAccessToken } from '../../services/api';
import FeedbackModal from './FeedbackModal';
const ENABLE_HEADER_FEEDBACK_REPLIES = false;

const SharedHeader = ({
  /** City dropdown in header; default off so first visits are not greeted with a picker (opt in per route). */
  showLocationPicker = false,
  liveVenueCount = null,
  showSearchBar = true,
  /** `stagTagline` — solo discovery strip: marquee tagline, no search icon / rotator. */
  searchStripVariant = 'default',
  /** Optional: called after city is selected (e.g. FSwipe refresh) */
  onCityChange,
  /** Optional page title shown under the header block (e.g. claim username) */
  title,
  /** Rendered inside the header card below search (and optional city row), e.g. homepage hero */
  children = null,
  /** Optional one-liner beside the city chip (e.g. signals preview on home). String or small React node. */
  signalsPreview = null,
  /** Scrolling demo ticker beside city chip (fake “live” signals — homepage). */
  signalsLiveDemo = false,
}) => {
  const [showCityDropdown, setShowCityDropdown] = useState(false);

  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const { user, isAuthenticated } = useAuth();
  const { unified, photoReviews } = useUnified();
  const { selectedCity, setSelectedCity, cityOptions = [] } = useCity();
  const { showInfo, showSuccess, showError } = useToast();
  const navigate = useNavigate();

  // Feature flag to enable the banner city chip selector
  const FEATURE_CITY_CHIP = (process.env.REACT_APP_FEATURE_CITY_CHIP || '').toString() === '1';
  const isAnonymous = !isAuthenticated;
  const cityListboxId = useId();
  const headerAvatar = useMemo(
    () =>
      resolveAccountAvatarDisplay({
        user,
        unifiedUser: unified?.user ?? null,
        fridaySwipeProfile: unified?.friday_swipe ?? null,
        photoReviews,
      }),
    [user, unified?.user, unified?.friday_swipe, photoReviews],
  );
  const headerAvatarUrl = headerAvatar.photoUrl || '';
  const headerAvatarInitials = headerAvatar.initials || '?';
  const headerAvatarPending = headerAvatar.isPendingReview;
  const { showImage: headerShowAvatarImage, onError: onHeaderAvatarError } =
    useAvatarImageFallback(headerAvatarUrl);

  useEffect(() => {
    if (!ENABLE_HEADER_FEEDBACK_REPLIES) return;
    // Fetch pending feedback replies and show toasts
    const fetchReplies = async () => {
      try {
        const token = user?.access_token || getCachedAccessToken();
        if (!isAuthenticated || !user?.id) return;
        const API_BASE = getAPIRoot();
        const res = await fetch(`${API_BASE}/feedback/replies`, {
          credentials: 'include',
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        if (!res.ok) {
          if (res.status === 404) return;
          return;
        }
        const json = await res.json();
        const replies = json?.replies || [];
        if (replies.length === 0) return;
        replies.forEach((r) => {
          const msg = typeof r.reply_message === 'string' ? r.reply_message : JSON.stringify(r.reply_message);
          showSuccess(`Reply from STAGSTER: ${msg}`);
        });
        const ids = replies.map(r => r.id);
        await fetch(`${API_BASE}/feedback/replies/ack`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          },
          body: JSON.stringify({ reply_ids: ids })
        });
      } catch (e) {
        if (String(e?.message || '').includes('404')) return;
        console.error('Failed to fetch feedback replies', e);
        showError(getErrorMessage(e));
      }
    };
    fetchReplies();
  }, [isAuthenticated, showError, showSuccess, user]);

  // UnifiedProvider refresh is triggered within the profile flow; no window events needed

  // Allow other components (e.g., legacy headers) to open the city picker when this header has showLocationPicker
  useEffect(() => {
    const openPicker = () => setShowCityDropdown(true);
    window.addEventListener('openCityPicker', openPicker);
    return () => {
      window.removeEventListener('openCityPicker', openPicker);
    };
  }, []);

  // Close city dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCityDropdown && !event.target.closest('.location-dropdown')) {
        setShowCityDropdown(false);
      }
    };
    const handleKeydown = (event) => {
      if (event.key === 'Escape' && showCityDropdown) setShowCityDropdown(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeydown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeydown);
    };
  }, [showCityDropdown]);

const cities = cityOptions.map((city) => city.name);
const liveCities = useMemo(() => {
  return cityOptions.filter((city) => city.stag_enabled || city.work_enabled).map((city) => city.name);
}, [cityOptions]);

const upcomingCities = useMemo(() => {
  return cities.filter((city) => !liveCities.includes(city));
}, [cities, liveCities]);

const handleCitySelect = useCallback(
  (city, source = 'dropdown', pickMeta = null) => {
    if (!city) return;
    setSelectedCity(city);
    try {
      localStorage.setItem('city_pref', city);
      localStorage.setItem('fridaystag_selected_city', city);
    } catch (_) {}

    try {
      analyticsService.track('city_change', { city, source });
    } catch (_) {}

    if (FEATURE_CITY_CHIP && !isAnonymous && user?.id) {
      (async () => {
        try {
          await authAPI.updateMe({
            userId: user.id,
            token: user?.access_token || getCachedAccessToken(),
            profile: { city }
          });
        } catch (err) {
          console.warn('city PATCH error', err);
        }
      })();
    }

    setShowCityDropdown(false);
    try {
      if (source === 'header_gps') {
        if (pickMeta?.isFallback) {
          showInfo(
            `Set to ${city} (outside live market range — you can change it in the list).`,
            { title: 'City', duration: 4000 },
          );
        } else {
          showSuccess(`Set to ${city} from your location.`, { title: 'City', duration: 3200 });
        }
      } else {
        showSuccess(`Now showing ${city}.`, { title: 'City', duration: 3000 });
      }
    } catch (_) {}

    if (typeof onCityChange === 'function') {
      try {
        onCityChange(city);
      } catch (_) {}
    }
  },
  [FEATURE_CITY_CHIP, isAnonymous, onCityChange, setSelectedCity, setShowCityDropdown, showInfo, showSuccess, user],
);

  const cityDropdownPanel = (
    <>
      {showCityDropdown && (
        <div
          id={cityListboxId}
          role="listbox"
          aria-label="Select city"
          className="absolute top-full left-1/2 -translate-x-1/2 z-[9999] mt-4 w-52 overflow-hidden rounded-2xl border border-[#ff2774]/30 bg-black/95 shadow-[0_32px_80px_rgba(0,0,0,0.9),0_0_30px_rgba(255,39,116,0.15)] backdrop-blur-2xl"
        >
          <div className="border-b border-white/10 px-4 pb-2 pt-3 flex justify-between items-center text-[10px] font-bold uppercase tracking-[0.25em] text-white/60">
            Select City
          </div>
          <button 
            type="button"
            className="w-full flex items-center gap-2.5 px-4 py-3 bg-[#ff2774]/10 hover:bg-[#ff2774]/20 border-b border-[#ff2774]/20 transition-colors text-left"
            onClick={() => {
              setShowCityDropdown(false);
              getUserCoordsOnce().then((res) => {
                if (!res.ok) {
                  if (res.code === GEO_PERMISSION_DENIED) {
                    showInfo('Location was denied. Pick your city from the list below, or re-enable location in the browser for next time.', {
                      title: 'City',
                      duration: 4500,
                    });
                  } else {
                    showInfo('Couldn’t detect location. Pick your city from the list in the header.', {
                      title: 'Location',
                      duration: 4000,
                    });
                  }
                  return;
                }
                const r = pickNearestLiveCity(res.lat, res.lng);
                handleCitySelect(r.city, 'header_gps', { isFallback: r.isFallback });
              });
            }}
          >
            <div className="flex h-4 w-4 items-center justify-center rounded-full bg-[#ff2774]/20 ring-1 ring-[#ff2774]/40">
              <span className="text-[10px]">📍</span>
            </div>
            <span className="text-[11px] font-bold tracking-widest uppercase text-[#ff2774]/90">Auto-Detect City</span>
          </button>
          {liveCities.map((city) => {
            const isActive = city === selectedCity;
            const cfg = cityOptions.find((item) => item.name === city) || {};
            const isLive = cfg.stag_enabled || cfg.work_enabled;
            return (
              <button
                role="option"
                aria-selected={isActive}
                key={city}
                type="button"
                onClick={() => handleCitySelect(city, 'header_dropdown')}
                className={`group flex w-full items-center justify-between border-l-2 px-4 py-3 text-sm font-medium transition-all ${
                  isActive
                    ? 'border-[#ff2774] bg-white/5 text-white'
                    : 'border-transparent text-white/60 hover:border-white/30 hover:bg-white/5 hover:text-white'
                }`}
              >
                <span className="flex items-center gap-3">
                  {isActive ? (
                    <div className="h-1.5 w-1.5 rounded-full bg-[#ff2774] shadow-[0_0_5px_#ff2774]" />
                  ) : (
                    <div className="h-1.5 w-1.5 rounded-full bg-white/20 transition-colors group-hover:bg-white/60" />
                  )}
                  <span className={`text-[11px] uppercase tracking-wider ${isActive ? 'font-bold' : 'font-medium'}`}>
                    {city}
                  </span>
                </span>
                <span
                  className={`font-mono text-[9px] uppercase tracking-[0.2em] ${
                    isLive ? (isActive ? 'text-[#ff2774]' : 'text-emerald-400/80') : 'text-white/20'
                  }`}
                >
                  {isActive ? 'NOW' : isLive ? 'LIVE' : 'SOON'}
                </span>
              </button>
            );
          })}
          {upcomingCities.length > 0 && (
            <>
              <div className="my-1 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
              {upcomingCities.map((city) => {
                const isActive = city === selectedCity;
                return (
                  <button
                    role="option"
                    aria-selected={isActive}
                    key={city}
                    type="button"
                    onClick={() => handleCitySelect(city, 'header_dropdown')}
                    className="group flex w-full items-center justify-between border-l-2 border-transparent px-4 py-2.5 text-sm font-medium text-white/40 transition-all hover:bg-white/5 hover:text-white/70"
                  >
                    <span className="flex items-center gap-3 pl-1.5">
                      <div className="h-1 w-1 rounded-full bg-white/10" />
                      <span className="text-[10px] uppercase tracking-wider">{city}</span>
                    </span>
                    <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/20">SOON</span>
                  </button>
                );
              })}
            </>
          )}
          {FEATURE_CITY_CHIP && (
            <div className="border-t border-white/10 bg-white/[0.02] px-3 py-2 text-center">
              <div className="font-mono text-[9px] uppercase tracking-[0.15em] text-white/30">Scanning sectors...</div>
            </div>
          )}
        </div>
      )}
    </>
  );

  return (
    <div className="shared-header-root">
      <div className="relative rounded-[30px] bg-[#060607] px-1 pb-2 pt-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.03)]">
        <div className="flex min-h-[48px] items-center justify-between gap-3 px-1 pb-1">
          <button
            type="button"
            onClick={() => {
              try {
                window.dispatchEvent(new CustomEvent('openMoreMenu'));
              } catch (_) {}
            }}
            className="relative flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-full bg-[linear-gradient(180deg,rgba(42,42,46,0.42),rgba(14,14,16,0.88))] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.06),0_0_14px_rgba(255,214,150,0.08)] ring-1 ring-white/10 transition-all duration-200 ease-out hover:shadow-[inset_0_0_0_1px_rgba(255,255,255,0.12),0_0_18px_rgba(255,255,255,0.12)] hover:ring-white/18 active:scale-[0.98]"
            aria-label="Open menu"
          >
            {isAuthenticated ? (
              <>
                <span className="pointer-events-none absolute inset-0 rounded-full border-[1.5px] border-[#ffcb84]/30" />
                <span className="absolute inset-[4px] overflow-hidden rounded-full bg-white/5">
                  <span className="relative flex h-full w-full items-center justify-center">
                    {headerShowAvatarImage ? (
                      <img
                        src={headerAvatarUrl}
                        alt=""
                        className="h-full w-full object-cover"
                        onError={onHeaderAvatarError}
                      />
                    ) : (
                      <span className="flex h-full w-full items-center justify-center text-[10px] font-semibold text-white/80">
                        {headerAvatarInitials}
                      </span>
                    )}
                    {headerAvatarPending ? (
                      <span
                        className="absolute -bottom-0.5 -right-0.5 flex h-[14px] w-[14px] items-center justify-center rounded-full bg-amber-400 text-[9px] font-bold leading-none text-black shadow ring-1 ring-black/30"
                        title="Photo pending review"
                      >
                        ?
                      </span>
                    ) : null}
                  </span>
                </span>
              </>
            ) : (
              <>
                <span className="absolute inset-0 animate-[mascot-ping_2.8s_ease-out_infinite] rounded-full border-[1.5px] border-[#ffcb84]/50" />
                <img src="/logo-mascot.webp" alt="" className="relative h-5 w-5 object-contain" />
              </>
            )}
          </button>

          <button
            type="button"
            onClick={() => navigate('/?tab=home')}
            className="flex shrink-0 cursor-pointer items-center justify-center gap-2 border-0 bg-transparent p-0"
            aria-label="STAGSTER home"
          >
            <img src="/logo-mascot.webp" alt="" className="h-7 w-auto object-contain" loading="eager" fetchpriority="high" />
            <span className="text-[17px] font-extrabold uppercase leading-none tracking-[0.14em] text-[#ff4d8d]">
              STAGSTER
            </span>
          </button>

          {/* Right side: City pill */}
          <div className="flex items-center gap-2 shrink-0">
            {/* Unified City Picker — always visible, always inline */}
            <div className="relative location-dropdown">
              <button
                type="button"
                onClick={() => setShowCityDropdown((prev) => !prev)}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.06] hover:bg-white/[0.08] transition-colors"
                aria-label="Change city"
              >
                <span className="text-[9px]">📍</span>
                <span className="text-[9px] font-bold uppercase tracking-wider text-[#ffcb84]">{selectedCity || 'HYD'}</span>
                <ChevronDown className={`w-2.5 h-2.5 text-white/50 transition-transform ${showCityDropdown ? 'rotate-180' : ''}`} />
              </button>
              {cityDropdownPanel}
            </div>
          </div>
        </div>

        <div className="mx-1 h-px bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" />

        {showSearchBar &&
          (searchStripVariant === 'stagTagline' ? (
            <StagDiscoverTaglineStrip />
          ) : (
            <PhraseRotator />
          ))}



        {children}
      </div>

      {title ? (
        <p className="mt-2 px-1 text-center text-[13px] font-medium text-white/75">{title}</p>
      ) : null}

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
      />
    </div>
  );
};

export default SharedHeader; 
